

const EOCD_SIGNATURE = 0x06054b50;
const CENTRAL_SIGNATURE = 0x02014b50;

interface ZipEntry {
  name: string;
  compression: number;
  compressedSize: number;
  localHeaderOffset: number;
}

function readCentralDirectory(view: DataView): ZipEntry[] {
  const limit = Math.max(0, view.byteLength - 0x10000 - 22);
  let eocd = -1;
  for (let i = view.byteLength - 22; i >= limit; i--) {
    if (view.getUint32(i, true) === EOCD_SIGNATURE) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) {
    throw new Error('Not a valid .xlsx file — no ZIP end-of-directory record found.');
  }

  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  const entries: ZipEntry[] = [];

  for (let i = 0; i < count; i++) {
    if (view.getUint32(offset, true) !== CENTRAL_SIGNATURE) break;

    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);

    entries.push({
      compression: view.getUint16(offset + 10, true),
      compressedSize: view.getUint32(offset + 20, true),
      name: new TextDecoder().decode(
        new Uint8Array(view.buffer, view.byteOffset + offset + 46, nameLength),
      ),
      localHeaderOffset: view.getUint32(offset + 42, true),
    });

    offset += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

async function readEntry(bytes: Uint8Array, view: DataView, entry: ZipEntry): Promise<string> {
  const local = entry.localHeaderOffset;
  const nameLength = view.getUint16(local + 26, true);
  const extraLength = view.getUint16(local + 28, true);
  const start = local + 30 + nameLength + extraLength;
  const raw = bytes.subarray(start, start + entry.compressedSize);

  if (entry.compression === 0) return new TextDecoder().decode(raw);
  if (entry.compression !== 8) {
    throw new Error(`${entry.name} uses an unsupported ZIP compression method.`);
  }

  const stream = new Blob([raw as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream('deflate-raw'));
  return new Response(stream).text();
}


function columnIndex(reference: string): number {
  let index = 0;
  for (const character of reference) {
    const code = character.charCodeAt(0);
    if (code < 65 || code > 90) break;
    index = index * 26 + (code - 64);
  }
  return index - 1;
}

function textOf(node: Element | null): string {
  if (!node) return '';
  return Array.from(node.getElementsByTagName('t'))
    .map((t) => t.textContent ?? '')
    .join('');
}

function parseXml(xml: string, what: string): Document {
  const document_ = new DOMParser().parseFromString(xml, 'application/xml');
  if (document_.getElementsByTagName('parsererror').length > 0) {
    throw new Error(`${what} inside the workbook is not valid XML.`);
  }
  return document_;
}

export async function readXlsx(file: File | Blob): Promise<string[][]> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);

  const entries = readCentralDirectory(view);
  const find = (name: string) => entries.find((e) => e.name === name);

  const workbookEntry = find('xl/workbook.xml');
  if (!workbookEntry) throw new Error('Not a valid .xlsx file — xl/workbook.xml is missing.');

  const workbook = parseXml(await readEntry(bytes, view, workbookEntry), 'xl/workbook.xml');
  const firstSheet = workbook.getElementsByTagName('sheet')[0];
  const relationshipId = firstSheet?.getAttribute('r:id') ?? firstSheet?.getAttribute('id') ?? null;

  let sheetPath = 'xl/worksheets/sheet1.xml';
  const relsEntry = find('xl/_rels/workbook.xml.rels');
  if (relationshipId && relsEntry) {
    const rels = parseXml(await readEntry(bytes, view, relsEntry), 'the workbook relationships');
    for (const relationship of Array.from(rels.getElementsByTagName('Relationship'))) {
      if (relationship.getAttribute('Id') !== relationshipId) continue;
      const target = relationship.getAttribute('Target') ?? '';
      sheetPath = target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`;
      break;
    }
  }

  const sheetEntry = find(sheetPath) ?? find('xl/worksheets/sheet1.xml');
  if (!sheetEntry) throw new Error('The workbook has no readable worksheet.');

  let shared: string[] = [];
  const sharedEntry = find('xl/sharedStrings.xml');
  if (sharedEntry) {
    const document_ = parseXml(await readEntry(bytes, view, sharedEntry), 'the shared strings');
    shared = Array.from(document_.getElementsByTagName('si')).map((si) => textOf(si));
  }

  const sheet = parseXml(await readEntry(bytes, view, sheetEntry), 'the worksheet');
  const rows: string[][] = [];
  let widest = 0;

  for (const row of Array.from(sheet.getElementsByTagName('row'))) {
    const cells: string[] = [];

    for (const cell of Array.from(row.getElementsByTagName('c'))) {
      const reference = cell.getAttribute('r') ?? '';
      const index = reference ? columnIndex(reference) : cells.length;
      const type = cell.getAttribute('t');

      let value: string;
      if (type === 's') {
        const at = Number(cell.getElementsByTagName('v')[0]?.textContent ?? '-1');
        value = shared[at] ?? '';
      } else if (type === 'inlineStr') {
        value = textOf(cell.getElementsByTagName('is')[0] ?? null);
      } else {
        value = cell.getElementsByTagName('v')[0]?.textContent ?? '';
      }

      while (cells.length < index) cells.push('');
      cells[index] = value.trim();
    }

    widest = Math.max(widest, cells.length);
    rows.push(cells);
  }

  for (const row of rows) {
    while (row.length < widest) row.push('');
  }

  return rows;
}


export function looksLikeExcelSerial(value: string): boolean {
  if (!/^\d{5}$/.test(value)) return false;
  const serial = Number(value);
  return serial >= 20000 && serial <= 60000;
}

export function fromExcelSerial(serial: number): string {
  const date = new Date(Date.UTC(1899, 11, 30) + serial * 86_400_000);
  return date.toISOString().slice(0, 10);
}
